import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { RiSearchLine } from "react-icons/ri";

import { ErrorComponent } from "~/components/error-boundary";
import { ItemList } from "~/components/item-list";
import { ProductCard } from "~/components/ui/cards/product-card";
import { PageContainer } from "~/components/ui/layout/page-container";
import { PageHeading } from "~/components/ui/page-heading";
import { Text } from "~/components/ui/typography";
import { DEFAULT_ITEMS_PER_PAGE_OPTION, PRODUCTS_SORTING_OPTIONS } from "~/config/item-list";
import { getFavorites } from "~/models/favorite.server";
import { getFilteredBrands } from "~/models/filter.server";
import { getProducts } from "~/models/product.server";
import { getSession } from "~/models/session.server";
import { useRootMatchData } from "~/root";
import { AppError } from "~/utils/errors";
import { getSeoMeta } from "~/utils/seo";

export const meta: MetaFunction = (metaArgs) => {
  const searchParams = new URLSearchParams(metaArgs.location.search);
  const search = searchParams.get("search") || "";

  const searchParamsKeys = Array.from(searchParams.keys());

  return getSeoMeta(metaArgs, {
    title: `Résultats de la recherche${search ? ` : ${search}` : ""}`,
    description: `Retrouvez les résultats de votre recherche${search ? ` : ${search}` : ""}`,
    noIndex: !searchParams || searchParamsKeys.length < 1 ? true : false,
    noFollow: !searchParams || searchParamsKeys.length < 1 ? true : false,
    selfCanonical: false,
    canonicalPath: `${metaArgs.location.pathname}${metaArgs.location.search}`,
  });
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  try {
    const session = await getSession({ request });
    const activeContactId = session.get("activeContactId") || null;

    const url = new URL(request.url);

    const [productsResponse, filteredBrandsResponse, favorites] = await Promise.all([
      getProducts({
        searchParams: url.searchParams,
        sortParam: url.searchParams.get("sort-by") || null,
        page: Number(url.searchParams.get("page") || 1),
        itemsPerPage: Number(
          url.searchParams.get("items-per-page") || DEFAULT_ITEMS_PER_PAGE_OPTION
        ),
        request,
      }),
      getFilteredBrands({
        searchParams: url.searchParams,
        request,
      }),
      activeContactId ? getFavorites({ contactId: activeContactId, request }) : null,
    ]);

    return json({
      products: productsResponse.data,
      totalItems: productsResponse.totalItems,
      filteredBrands: filteredBrandsResponse.data,
      favorites,
      errors: null,
    });
  } catch (error) {
    if (error instanceof AppError) {
      return json({
        products: null,
        totalItems: null,
        filteredBrands: null,
        favorites: null,
        comparator: null,
        errors: error.getAllErrors(),
      });
    }

    throw error;
  }
};

const SearchResultsPage = () => {
  const rootData = useRootMatchData();
  const { products, totalItems, filteredBrands } = useLoaderData<typeof loader>();

  const breadcrumbData = [{ name: "Resultats de la recherche", slug: "" }];

  return (
    <PageContainer>
      <PageHeading
        breadcrumbData={breadcrumbData}
        HeadingIconComponent={<RiSearchLine />}
        headingText={"Resultats de la recherche"}
      />
      <ItemList
        category={null}
        characteristicFilters={[]}
        totalItemsText={`Nombre de produits : ${totalItems || 0}`}
        sortingOptions={[{ label: "Pertinence", value: "" }, ...PRODUCTS_SORTING_OPTIONS]}
        totalItems={totalItems || 0}
        defaultSortingOption={""}
        brands={filteredBrands}
        comparator={rootData?.comparator || null}
        isUserConnected={!!rootData?.contact?.id}
        isEmpty={!products || products.length === 0}
      >
        <>
          {products && products.length > 0 ? (
            products.map((product) => {
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  comparator={rootData?.comparator || null}
                  contact={rootData?.contact || null}
                />
              );
            })
          ) : (
            <Text muted className="mx-auto mb-8 text-center">
              Aucun produit n&apos;a été trouvé.
              <br />
              Essayez de modifier vos filtres.
            </Text>
          )}
        </>
      </ItemList>
    </PageContainer>
  );
};

export default SearchResultsPage;

export const ErrorBoundary = () => {
  return <ErrorComponent />;
};
